import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DOMPurify from "dompurify";
import { CircularProgress } from "@mui/material";
import { calculateOrderSummary, newSendMail } from "../data/helpers";
import { getCart, getContacts, getStores } from "../redux/selectors";
import { clearCart } from "../redux/cart/slice";
import { setContacts } from "../redux/user/slice";
import { toast } from "react-toastify";
import { storeId } from "../data/constants";

const MailForm = ({ handleClose }) => {
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();

  const order = useSelector(getCart);
  const contacts = useSelector(getContacts);
  const stores = useSelector(getStores);
  const currentStore = stores.find((store) => store._id === storeId);
  const hasDelivery = currentStore ? currentStore.hasDelivery : false;

  const isFrProducts = order.some((product) => product.packageSize === "fr");

  const { name, email, phone, address } = contacts;
  const defaultFormData = {
    name: name || "",
    email: email || "",
    phone: phone || "",
    participant: isFrProducts ? "" : undefined,
    address: address || "",
    organization: isFrProducts ? "" : undefined,
    division: isFrProducts ? "" : undefined,
    paymentMethod: isFrProducts ? "e-transfer" : undefined,
    deliveryMethod: isFrProducts || !hasDelivery ? "pickup" : "delivery",
    message: "",
    consent: true,
  };
  const [formData, setFormData] = useState({ ...defaultFormData });

  const cartList = useSelector(getCart);

  const cartListTotalQty = cartList.reduce((acc, curr) => {
    return acc + curr.qty * 0.05;
  }, 0);

  const [orderData, setOrderData] = useState(null);

  useEffect(() => {
    setOrderData(calculateOrderSummary(cartList, storeId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartList.length, cartListTotalQty]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : DOMPurify.sanitize(value),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    dispatch(
      setContacts({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
      })
    );

    if (!orderData) {
      toast.warning("No data for order");
    } else {
      const { subtotal, taxGST, taxPST, delivery, total } = orderData;

      // Normalize values to numbers and format to 2 decimals (strings)
      const subtotalStr = Number(subtotal).toFixed(2);
      const taxGSTStr = Number(taxGST).toFixed(2);
      const taxPSTStr = Number(taxPST).toFixed(2);

      const deliveryNum =
        formData.deliveryMethod === "pickup" || !hasDelivery
          ? 0
          : Number(delivery);
      const deliveryStr = Number(deliveryNum).toFixed(2);

      let totalNum;
      if (formData.deliveryMethod === "pickup") {
        // when pickup, total was calculated including delivery; remove delivery
        totalNum = Number(total) - Number(delivery);
      } else {
        totalNum = Number(total);
      }
      const totalStr = Number(totalNum).toFixed(2);

      await newSendMail({
        ...formData,
        storeEmail: currentStore.email,
        storeId: currentStore._id,
        order,
        subtotal: subtotalStr,
        taxGST: taxGSTStr,
        taxPST: taxPSTStr,
        delivery: deliveryStr,
        total: totalStr,
      });
    }

    setLoading(false);

    e.target.reset();
    handleClose();
    setFormData({ ...defaultFormData }); // Reset form
    dispatch(clearCart());
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col items-center w-full"
      id="mailForm"
    >
      <InputField
        onChange={handleChange}
        name="name"
        placeholder="Your Full Name"
        value={formData.name}
        pattern="[A-Za-z\s]+"
        title="Name should only contain letters and spaces."
      />
      <InputField
        onChange={handleChange}
        type="email"
        name="email"
        placeholder="Email"
        value={formData.email}
        pattern="^\S+@\S+\.\S+$"
        title="Please enter a valid email address."
      />
      <InputField
        onChange={handleChange}
        type="tel"
        name="phone"
        placeholder="Phone Number"
        value={formData.phone}
        maxLength="15"
        pattern="^\d{10,15}$"
        title="Phone number should be 10-15 digits long."
      />
      {isFrProducts && (
        <>
          <InputField
            onChange={handleChange}
            name="organization"
            placeholder="Organization/Group/School Name"
            value={formData.organization}
          />
          <InputField
            onChange={handleChange}
            name="participant"
            placeholder="Student/Participant Name"
            value={formData.participant}
          />
          <InputField
            onChange={handleChange}
            name="division"
            placeholder="Grade/Division (if applicable)"
            value={formData.division}
            required={false}
          />
        </>
      )}
      <InputField
        onChange={handleChange}
        name="address"
        placeholder="Full Address"
        value={formData.address}
        maxLength="100"
        required={
          isFrProducts || !hasDelivery
            ? false
            : formData.deliveryMethod === "delivery"
        }
      />
      {!isFrProducts && hasDelivery && (
        <div className="flex flex-col w-full mb-3">
          <label className="mb-1 text-sm font-medium">Delivery Method</label>
          <div className="flex gap-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="deliveryMethod"
                value="pickup"
                checked={formData.deliveryMethod === "pickup"}
                onChange={handleChange}
                className="mr-2 w-4 h-4"
              />
              Pickup
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="deliveryMethod"
                value="delivery"
                checked={formData.deliveryMethod === "delivery"}
                onChange={handleChange}
                className="mr-2 w-4 h-4"
              />
              Delivery
            </label>
          </div>
          <p className="w-full text-xs text-gray-500 mb-3">
            Choosing <span className="font-semibold">Pickup</span> removes the
            delivery fee from your final total.
          </p>
        </div>
      )}
      <textarea
        name="message"
        placeholder="Your Message"
        value={formData.message}
        onChange={handleChange}
        className="mb-2 md:mb-3 p-1 border rounded-md w-full"
        maxLength="200"
        autoComplete="off"
        rows={2}
      />
      {isFrProducts && (
        <PaymentRadioGroup
          paymentMethod={formData.paymentMethod}
          handleChange={handleChange}
        />
      )}
      <div className="flex w-full px-2 items-center mb-3 md:mb-4">
        <input
          type="checkbox"
          name="consent"
          checked={formData.consent}
          onChange={handleChange}
          className="mr-2 w-4 h-4"
          id="consent"
        />
        <label htmlFor="consent" className="text-xs text-gray-500">
          I agree to receive email updates and notifications
        </label>
      </div>
      {/* {sitekey && (
        <ReCAPTCHA
          sitekey={sitekey}
          onChange={handleCaptchaChange}
          className="mb-3 md:mb-4 scale-90 md:scale-100"
        />
      )} */}
      <div className="relative w-full flex justify-center">
        <button
          type="submit"
          disabled={loading}
          className={`btn  border border-mainBlack text-mainBlack w-1/2 ${
            loading
              ? "cursor-not-allowed bg-bluredBlack hover:text-mainBlack hover:border-mainBlack "
              : "hover:text-mainRed hover:border-mainRed bg-mainGreen"
          }`}
        >
          Submit
        </button>
        {loading && (
          <CircularProgress
            size={54}
            sx={{
              color: "#c41d1844",
              position: "absolute",
              top: -11,
              left: "calc(50% - 27px)",
              zIndex: 1,
            }}
          />
        )}
      </div>
    </form>
  );
};

export default MailForm;

function InputField({
  type = "text",
  required = true,
  maxLength = "50",
  onChange,
  ...rest
}) {
  return (
    <div className="relative w-full">
      <input
        {...rest}
        onChange={onChange}
        type={type}
        required={required}
        maxLength={maxLength}
        className="mb-2 md:mb-3 p-1 border bg-transparent rounded-md w-full required"
        autoComplete={rest.name}
      />
      {required && rest.value === "" && (
        <span className="required absolute top-2 right-2 text-mainRed text-2xl">
          *
        </span>
      )}
    </div>
  );
}

function PaymentRadioGroup({ paymentMethod, handleChange }) {
  const options = ["e-transfer", "cheque", "cash"];

  return (
    <div className="flex flex-col w-full mb-3">
      <label className="mb-1 text-sm font-medium">
        <div>Payment method </div>
        <div className="text-mainRed text-xs">
          Selecting method won’t charge you now
        </div>
      </label>
      <div className="flex gap-4">
        {options.map((option) => (
          <label className="inline-flex items-center" key={option}>
            <input
              type="radio"
              name="paymentMethod"
              value={option}
              checked={paymentMethod === option}
              onChange={handleChange}
              className="mr-2 w-4 h-4"
            />
            {option}
          </label>
        ))}
      </div>
    </div>
  );
}
